import { useLoaderData, useSearchParams } from "@remix-run/react";
import { BlogPostCard } from "#app/components/blog/index.js";
import { getBlogPostMetadata, REGION_LABELS } from "#app/utils/blog.js";
import {
  Layout,
  Header,
  Content,
} from "#app/components/layouts/MarketingPage.jsx";
import {
  Stack,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Text,
} from "@chakra-ui/react";
import { json } from "@remix-run/node";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { withSecurityHeaders } from "#app/utils/security.server.js";

const REGION_KEYS = Object.keys(REGION_LABELS);

const REGION_DESCRIPTIONS = {
  all: "Latest camping guides, booking tips, and availability updates from Australia, New Zealand, and Canada",
  au: "Australian camping guides. Booking systems, cancellation alerts, and tips for the most popular campgrounds",
  nz: "New Zealand tramping guides. Great Walks booking dates, DOC hut tips, and cancellation alerts",
  ca: "Canadian camping guides. Booking systems, cancellation alerts, and tips for the most popular campgrounds",
};

export const meta = ({ data, matches }) => {
  const baseUrl = getBaseUrl(matches);
  const region = data?.activeRegion;
  const regionName = region ? REGION_LABELS[region] : null;
  const title = regionName
    ? `${regionName} Camping Blog | Schnerp`
    : "Camping Blog | Schnerp";
  const description = REGION_DESCRIPTIONS[region] || REGION_DESCRIPTIONS.all;

  return getSeoMeta({
    title,
    description,
    url: `${baseUrl}/blog`,
    image: getDefaultOgImage(baseUrl),
  });
};

export const headers = ({ loaderHeaders, parentHeaders }) =>
  withSecurityHeaders({
    ...Object.fromEntries(parentHeaders),
    "Cache-Control": loaderHeaders.get("Cache-Control"),
  });

export const loader = async ({ request }) => {
  const url = new URL(request.url);
  const regionParam = url.searchParams.get("region");
  const activeRegion = REGION_KEYS.includes(regionParam) ? regionParam : null;

  const posts = await getBlogPostMetadata();
  return json(
    { posts, activeRegion },
    {
      headers: {
        "Cache-Control":
          "public, max-age=0, s-maxage=86400, stale-while-revalidate=86400",
      },
    },
  );
};

export default function BlogIndex() {
  const { posts, activeRegion } = useLoaderData();
  const [, setSearchParams] = useSearchParams();

  // Build tab data: "All" + each region
  const allTab = { key: "all", label: "All", posts };
  const regionTabs = REGION_KEYS.map((key) => {
    const regionPosts = posts.filter((p) => p.region === key);
    return {
      key,
      label: REGION_LABELS[key],
      posts: regionPosts,
    };
  });
  const tabs = [allTab, ...regionTabs];

  const activeIndex = activeRegion ? REGION_KEYS.indexOf(activeRegion) + 1 : 0;

  const handleTabChange = (index) => {
    if (index === 0) {
      setSearchParams({});
    } else {
      setSearchParams({ region: REGION_KEYS[index - 1] });
    }
  };

  const subtitle = REGION_DESCRIPTIONS[activeRegion] || REGION_DESCRIPTIONS.all;

  return (
    <Layout>
      <Header title="Around the campfire" subtitle={subtitle} />
      <Content>
        <Tabs
          index={activeIndex}
          onChange={handleTabChange}
          variant="solid-rounded"
          colorScheme="brand"
          size={{ base: "sm", md: "md" }}
        >
          <TabList
            overflowX="auto"
            flexWrap="nowrap"
            sx={{ WebkitOverflowScrolling: "touch" }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} mr={2} whiteSpace="nowrap">
                {tab.label} ({tab.posts.length})
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel key={tab.key} px={0}>
                {tab.posts.length > 0 ? (
                  <Stack spacing={12}>
                    {tab.posts.map((post) => (
                      <BlogPostCard key={post.slug} post={post} />
                    ))}
                  </Stack>
                ) : (
                  <Text
                    color="muted"
                    textAlign="center"
                    py={12}
                    role="status"
                    aria-live="polite"
                  >
                    No posts yet for {tab.label}. Check back soon — we're
                    working on it.
                  </Text>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Content>
    </Layout>
  );
}
